import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import httpRequest from "../utils/httpRequest";
import InvoiceTable from "./InvoiceTable";
import CreateInvoiceDialog from "./CreateInvoiceDialog";
import FinancialYearDialog from "./FinancialYearDialog";
import HSNSelector from "./HSNSelector";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import InvoiceFilters from "./InvoiceFilters";
import { getLoggedInUserRole } from "../utils/auth";
import CreateCreditNoteInvoiceDialog from "./CreateCreditNoteInvoiceDialog";

const Invoice = () => {
  const role = getLoggedInUserRole();
  const [invoices, setInvoices] = useState([]);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);
  const [creditNoteOpenInvoiceDialog, setCreditNoteOpenInvoiceDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [invoiceType, setInvoiceType] = useState(null);
  const [openFinancialDialog, setOpenFinancialDialog] = useState(false);
  const [filters, setFilters] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (type) => {
    setDialogLoading(true);
    handleMenuClose();
    setTimeout(() => {
      setInvoiceType(type);
      setOpenInvoiceDialog(true);
      setDialogLoading(false);
    }, 300);
  };

  const handleCreditNoteSelect = (type) => {
    setDialogLoading(true);
    handleMenuClose();
    setTimeout(() => {
      setInvoiceType(type);
      setCreditNoteOpenInvoiceDialog(true);
      setDialogLoading(false);
    }, 300);
  };

  const fetchInvoices = async (page, pageSize, filtersOverride) => {
    setLoading(true);
    try {
      const invoiceData = await httpRequest(
        "invoice/getInvoicesData",
        "GET",
        {},
        { page, pageSize, ...(filtersOverride ?? filters) }
      );
      setInvoices(invoiceData);
    } catch (err) {
      setError("Failed to fetch invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices(page, pageSize);
  }, [page, pageSize]);

  const handleApplyFilters = (filtersPayload) => {
    setPage(1);
    const toApply = filtersPayload ?? filters;
    fetchInvoices(1, pageSize, toApply);
  };

  const handleCloseInvoiceDialogWithAlert = () => {
    if (window.confirm('Are you sure you want to close? All unsaved progress will be lost.')) {
      setOpenInvoiceDialog(false);
      setInvoiceType(null);
    }
  };

  const handleCloseInvoiceDialog = () => {
    setOpenInvoiceDialog(false);
    setInvoiceType(null);
  };

  const handleCreditNoteCloseInvoiceDialog = () => {
    setCreditNoteOpenInvoiceDialog(false);
    setInvoiceType(null);
  };

  const handleCloseFinancialDialog = () => {
    setOpenFinancialDialog(false);
  };

  const handleInvoiceCreated = (newInvoice) => {
    fetchInvoices(page, pageSize);
    setSnackbar({
      open: true,
      message: "Invoice created successfully!",
      severity: "success",
    });
  };

  const handleFinancialYearCreated = () => {
    setSnackbar({
      open: true,
      message: "Financial Year added successfully!",
      severity: "success",
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        marginTop: 2,
        width: "100%",
      }}
    >
      <Typography variant="h4" gutterBottom>
        Invoice Management
      </Typography>
      <Box
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <div></div>
        <div style={{ display: "flex" }}>
          <Button
            variant="contained"
            onClick={handleMenuClick}
            sx={{ ml: 1 }}
            startIcon={<NoteAddIcon />}
          >
            Create Invoice
          </Button>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => handleSelect("gst")}>GST Invoice</MenuItem>
            {role !== "auditor" && (
              <MenuItem onClick={() => handleSelect("kachcha")}>
                Kachcha Invoice
              </MenuItem>
            )}
            <MenuItem onClick={() => handleCreditNoteSelect("credit-note")}>Credit Note</MenuItem>
          </Menu>
        </div>
      </Box>
      <InvoiceFilters
        filters={filters}
        setFilters={setFilters}
        onApply={handleApplyFilters}
        sx={{ width: "100%" }}
      />
      
      {loading ? (
        <Typography variant="h6">Loading...</Typography>
      ) : error ? (
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      ) : (
        <InvoiceTable
          invoices={invoices}
          setPageSize={setPageSize}
          setPage={setPage}
          pageSize={pageSize}
          page={page}
          fetchInvoices={fetchInvoices}
        />
      )}
      {dialogLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <CircularProgress />
        </Box>
      )}
      <CreateInvoiceDialog
        open={openInvoiceDialog}
        onClose={handleCloseInvoiceDialog}
        onCloseAlert={handleCloseInvoiceDialogWithAlert}
        onInvoiceCreated={handleInvoiceCreated}
        type={invoiceType}
      />

      <CreateCreditNoteInvoiceDialog
        open={creditNoteOpenInvoiceDialog}
        onClose={handleCreditNoteCloseInvoiceDialog}
        onInvoiceCreated={handleInvoiceCreated}
        type={invoiceType}
      />

      <FinancialYearDialog
        open={openFinancialDialog}
        onClose={handleCloseFinancialDialog}
        onFinancialYearCreated={handleFinancialYearCreated}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Invoice;
